import React, { Component } from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import { Scrollbars } from 'react-custom-scrollbars';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import Typography from '@material-ui/core/Typography';

import Menu from 'components/common/Menu';
import Button from 'components/common/Button';
import Chip from 'components/common/Chip';
import CheckboxField from 'components/fields/CheckboxField';
import IconPlus from 'components/icons/IconPlus';

import hexToRgb from 'styles/hexToRgb';

const styles = theme => ({
  grid: {
    boxSizing: 'border-box',
    marginLeft: 7,
    border: `1px dashed ${theme.palette.primary.A400}`,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
    },
  },
  gridChips: {
    flexShrink: 'unset',
  },
  gridControls: {
    position: 'relative',
    padding: 2,
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      bottom: '100%',
      left: 0,
      right: 0,
      height: 5,
      background: `linear-gradient(
        to bottom,
        rgba(${hexToRgb(theme.palette.secondary.A100)}, 0) 0%,
        rgba(${hexToRgb(theme.palette.secondary.A100)}, 0.05) 100%
      )`,
    },
  },
  iconPlus: {
    width: 14,
    height: 14,
    marginTop: -2,
    marginRight: 13,
  },
  buttonAddDay: {
    margin: '2px 4px 2px 2px',
    color: theme.palette.add[500],
  },
  buttonClearDays: {
    fontWeight: 400,
    textTransform: 'none',
    color: theme.palette.grey[700],
  },
  checkboxField: {
    display: 'flex',
    margin: 0,
    paddingLeft: 4,
    width: 260,
    [theme.breakpoints.down('xs')]: {
      width: 'auto',
    },
    '& + $checkboxField': {
      borderTop: `1px solid ${theme.palette.primary.A100}`,
    },
  },
  chip: {
    margin: '2px 4px',
  },
});

class WeekdayField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      weekdays: [],
    };

    this.onAddDayButtonClick = this.onAddDayButtonClick.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onCheckboxChange = this.onCheckboxChange.bind(this);
    this.onClearDaysButtonClick = this.onClearDaysButtonClick.bind(this);
    this.onSubmitDaysButtonClick = this.onSubmitDaysButtonClick.bind(this);
    this.onChipDelete = this.onChipDelete.bind(this);
  }

  onAddDayButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      weekdays: this.props.input.value,
    });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onCheckboxChange(id) {
    const { weekdays } = this.state;
    const index = weekdays.indexOf(id);
    if (index === -1) {
      this.setState({ weekdays: [
        ...weekdays.slice(0),
        id,
      ].sort() });
    } else {
      this.setState({ weekdays: [
        ...weekdays.slice(0, index),
        ...weekdays.slice(index + 1),
      ] });
    }
  }

  onClearDaysButtonClick() {
    const weekdays = this.props.input.value || [];
    this.setState({ weekdays });
  }

  onSubmitDaysButtonClick() {
    const { weekdays } = this.state;
    this.setState({ anchorEl: null });
    this.props.input.onChange(weekdays);
  }

  onChipDelete(id) {
    const { value } = this.props.input;
    const index = value.indexOf(id);
    const weekdays = [
      ...value.slice(0, index),
      ...value.slice(index + 1),
    ];
    this.props.input.onChange(weekdays);
  }

  render() {
    const { input, customise, classes } = this.props;
    const { anchorEl, weekdays } = this.state;
    const open = Boolean(anchorEl);
    const weekdayNames = moment().localeData().weekdays();
    const weekdayNamesShort = moment().localeData().weekdaysShort();
    let label;

    if (!customise) {
      switch (input.value.join('')) {
        case '12345':
          label = 'Weekdays';
          break;
        case '60':
          label = 'Weekends';
          break;
        default:
          label = '';
      }
    }

    return (
      <FormControl>
        {customise ? (
          <Grid
            className={classes.grid}
            container
            alignItems="flex-start"
            spacing={0}
            wrap="nowrap"
          >
            <Grid item>
              <Button
                className={classes.buttonAddDay}
                color="inherit"
                size="sm"
                onClick={this.onAddDayButtonClick}
              >
                <IconPlus className={classes.iconPlus} />
                Add Day
              </Button>
            </Grid>
            <Grid
              className={classes.gridChips}
              item
            >
              {input.value && input.value.map(id => (
                <Chip
                  key={id}
                  className={classes.chip}
                  size="md"
                  label={weekdayNamesShort[id - 1].toUpperCase()}
                  onDelete={() => {
                    this.onChipDelete(id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      this.onChipDelete(id);
                    }
                  }}
                />
              ))}
            </Grid>
            <Menu
              id={`${input.name}-weekdays-menu`}
              anchorEl={anchorEl}
              open={open}
              onClose={this.onMenuClose}
              arrow={false}
              placement="bottom-left"
              padding="none"
              list={false}
              maxHeight={240}
            >
              <Scrollbars
                autoHeight
                autoHeightMin={0}
                autoHeightMax={200}
                thumbMinSize={30}
                universal
              >
                {[1, 2, 3, 4, 5, 6, 7].map(id => (
                  <CheckboxField
                    key={id}
                    className={classes.checkboxField}
                    id={`weekday-${id}`}
                    label={weekdayNames[id - 1]}
                    input={{
                      name: `weekday-${id}`,
                      value: weekdays.indexOf(id) !== -1,
                      onChange: () => { this.onCheckboxChange(id); },
                    }}
                    meta={{
                      touched: false,
                      active: false,
                    }}
                  />
                ))}
              </Scrollbars>
              <Grid
                className={classes.gridControls}
                container
                alignItems="center"
                justify="space-between"
                spacing={0}
                wrap="nowrap"
              >
                <Grid item>
                  <Button
                    className={classes.buttonClearDays}
                    color="inherit"
                    onClick={this.onClearDaysButtonClick}
                  >
                    Clear
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    color="primary"
                    onClick={this.onSubmitDaysButtonClick}
                  >
                    Apply
                  </Button>
                </Grid>
              </Grid>
            </Menu>
          </Grid>
        ) : (
          <Grid
            container
            alignItems="flex-start"
            spacing={8}
            wrap="nowrap"
          >
            <Grid item>
              {label &&
                <Typography
                  variant="subheading"
                  color="secondary"
                >
                  {label}
                </Typography>
              }
            </Grid>
            <Grid item>
              <Typography variant="subheading">
                {input.value && input.value.map(id => (
                  weekdayNamesShort[id - 1]
                )).join(', ')}
              </Typography>
            </Grid>
          </Grid>
        )}
      </FormControl>
    );
  }
}

WeekdayField.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.number),
      PropTypes.string,
    ]).isRequired,
    onChange: PropTypes.func,
  }).isRequired,
  customise: PropTypes.bool,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
    gridControls: PropTypes.string.isRequired,
    gridChips: PropTypes.string.isRequired,
    iconPlus: PropTypes.string.isRequired,
    buttonAddDay: PropTypes.string.isRequired,
    buttonClearDays: PropTypes.string.isRequired,
    checkboxField: PropTypes.string.isRequired,
    chip: PropTypes.string.isRequired,
  }).isRequired,
};

WeekdayField.defaultProps = {
  customise: false,
};

export default withStyles(styles)(WeekdayField);



// WEBPACK FOOTER //
// ./src/components/fields/WeekdayField.js